import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@capsico/ui';
import { CUSTOMER_LOCALES } from '@capsico/contracts';
import logo from '../components/capsico-logo.png';
import { LOCALE_NAMES, useI18n } from '../i18n';
import { useSession } from '../hooks/useSession';
import { useProfileQuery } from '../hooks/useProfile';
import { useTheme, type ThemeChoice } from '../hooks/useTheme';
import { useMyReviews, type RateTarget } from '../hooks/useReviews';
import { RateStrip } from '../components/Reviews';
import { BellButton } from '../components/Inbox';
import { useUnreadCount } from '../hooks/useInbox';
import { RateFlow } from './RateFlow';
import { useLegalLinks, useLegalPending, type LegalLink } from '../hooks/useLegal';
import { Button, Card, ListSkeleton, Pill, SectionTitle } from '../components/ui';

const THEMES: ThemeChoice[] = ['auto', 'light', 'dark'];

/** Account (C10): profile header or signed-out card, addresses, language, appearance, sign out. */
export function AccountPage() {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const profile = useProfileQuery(session.signedIn);
  const theme = useTheme();
  const mine = useMyReviews(session.signedIn);
  const [rateTarget, setRateTarget] = useState<RateTarget | null>(null);
  const firstPending = mine.pending[0] ?? null;
  const unread = useUnreadCount(session.signedIn);
  const legal = useLegalPending(session.signedIn);
  const legalLinks = useLegalLinks(legal.country);
  const legalLabel: Record<LegalLink['key'], string> = {
    terms_customer: t('legal.terms'),
    privacy: t('legal.privacy'),
    refund_cancellation: t('legal.refunds'),
    cookie: t('legal.cookies'),
    terms_restaurant: t('legal.restaurantTerms'),
    terms_rider: t('legal.riderTerms'),
    grievance: t('legal.grievance'),
  };
  const themeLabel: Record<ThemeChoice, string> = {
    auto: t('account.themeAuto'),
    light: t('account.themeLight'),
    dark: t('account.themeDark'),
  };

  return (
    <div className="min-h-dvh bg-page">
      <header className="sticky top-0 z-30 flex h-14 items-center bg-surface px-4 shadow-[inset_0_-1px_0_var(--color-hair)]">
        <h1 className="flex-1 text-lg font-semibold text-ink">{t('account.title')}</h1>
        {session.signedIn && (
          <BellButton
            label={unread ? t('inbox.bellUnread', { count: unread }) : t('inbox.bell')}
            count={unread}
            onClick={() => navigate('/account/inbox')}
          />
        )}
      </header>
      <div className="flex flex-col gap-5 px-4 pt-5 pb-8">
        {session.loading ? (
          <ListSkeleton rows={2} />
        ) : session.signedIn ? (
          <Card>
            <div className="flex items-center gap-3">
              <span className="inline-flex size-12 items-center justify-center rounded-full bg-brand text-lg font-bold text-on-brand">
                {(profile.data?.name ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xl font-semibold text-ink">
                  {profile.data?.name ?? session.me?.name ?? ''}
                </p>
                <p className="text-xs text-ink-2 tabular">{session.me?.phoneMasked}</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/account/profile')}
                className="text-sm font-semibold text-brand-ink"
              >
                {t('common.edit')}
              </button>
            </div>
          </Card>
        ) : (
          <Card className="text-center">
            <img src={logo} alt="" className="mx-auto size-12 rounded-xl" />
            <p className="mt-3 text-base font-bold text-ink">{t('account.signedOutTitle')}</p>
            <p className="mt-1 text-sm text-ink-2">{t('account.signedOutBody')}</p>
            <Button className="mt-4" full onClick={() => navigate('/sign-in?next=/account')}>
              {t('account.signIn')}
            </Button>
          </Card>
        )}

        {session.signedIn && firstPending && (
          <RateStrip
            title={t('rate.stripTitle')}
            body={
              mine.pending.length > 1
                ? t('rate.stripMany', {
                    name: firstPending.outletName,
                    count: mine.pending.length - 1,
                  })
                : t('rate.stripOne', { name: firstPending.outletName })
            }
            actionLabel={t('rate.stripAction')}
            onRate={() => setRateTarget({ kind: 'new', pending: firstPending })}
            onOpenAll={() => navigate('/account/reviews')}
          />
        )}

        {session.signedIn && (
          <Card flush>
            <Row
              icon="receipt"
              label={t('account.orders')}
              onClick={() => navigate('/account/orders')}
            />
            <Row
              icon="wallet"
              label={t('account.wallet')}
              onClick={() => navigate('/account/wallet')}
            />
            <Row
              icon="pin"
              label={t('account.addresses')}
              onClick={() => navigate('/account/addresses')}
            />
            <Row
              icon="star"
              label={t('account.reviews')}
              onClick={() => navigate('/account/reviews')}
            />
            <Row icon="info" label={t('help.title')} onClick={() => navigate('/account/help')} />
          </Card>
        )}

        {session.signedIn && (
          <Card flush>
            <Row
              icon="gift"
              label={t('account.refer')}
              onClick={() => navigate('/account/refer')}
            />
            <Row
              icon="users"
              label={t('account.campus')}
              onClick={() => navigate('/account/campus-pass')}
            />
            <Row
              icon="bell"
              label={t('account.notifications')}
              onClick={() => navigate('/account/notifications')}
            />
          </Card>
        )}

        <section>
          <SectionTitle>{t('legal.title')}</SectionTitle>
          <Card flush>
            {legalLinks.map((link) => (
              <Row
                key={link.key}
                icon={
                  link.key === 'grievance' ? 'mail' : link.key === 'privacy' ? 'shield' : 'file'
                }
                label={legalLabel[link.key]}
                onClick={() =>
                  link.path ? navigate(link.path) : window.open(link.url, '_blank', 'noopener')
                }
              />
            ))}
          </Card>
        </section>

        <section>
          <SectionTitle>{t('account.language')}</SectionTitle>
          <div
            role="radiogroup"
            aria-label={t('account.language')}
            className="flex flex-wrap gap-2"
          >
            {CUSTOMER_LOCALES.map((code) => (
              <Pill key={code} active={locale === code} onClick={() => setLocale(code)}>
                {LOCALE_NAMES[code]}
              </Pill>
            ))}
          </div>
        </section>

        <section>
          <SectionTitle>{t('account.theme')}</SectionTitle>
          <div role="radiogroup" aria-label={t('account.theme')} className="flex flex-wrap gap-2">
            {THEMES.map((choice) => (
              <Pill
                key={choice}
                active={theme.choice === choice}
                onClick={() => theme.setChoice(choice)}
              >
                {themeLabel[choice]}
              </Pill>
            ))}
          </div>
        </section>

        {session.signedIn && (
          <Button tone="danger" full busy={session.signingOut} onClick={session.signOut}>
            {t('account.signOut')}
          </Button>
        )}
        <p className="text-center text-caption text-ink-3">
          {t('account.version', { version: __APP_VERSION__ })}
        </p>
      </div>
      {rateTarget && rateTarget.kind === 'new' && (
        <RateFlow
          key={rateTarget.pending.orderId}
          target={rateTarget}
          onClose={() => setRateTarget(null)}
        />
      )}
    </div>
  );
}

function Row({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-tile"
    >
      <span className="inline-flex size-9 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name={icon} size={18} />
      </span>
      <span className="flex-1 text-sm font-medium text-ink">{label}</span>
      <Icon name="chevron-right" size={16} />
    </button>
  );
}
